import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { prMergeView, type GithubPrLive, type TaskPrsLiveResponse } from '@shared/github-pr';
import type { TaskParent, TaskPr, TaskSubtaskCount, TaskWaiting } from '@shared/types/tasks';
import { AppAlert } from '@/components/AppAlert';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadError } from '@/components/LoadError';
import { useLoader } from '@/hooks/use-loader';
import { usePermission } from '@/hooks/usePermission';
import { describeApiError } from '@/lib/api-errors';
import { useCurrentProject } from '@/lib/current-project';
import { listRuns } from '@/lib/runs-api';
import { fixTaskPrConflicts, getTaskPrsLive, mergeTaskPr, updateTaskPr } from '@/lib/tasks-api';
import { runPath } from '@/navigation/project-paths';
import { useTaskStore } from '@/stores/taskStore';
import { mergeRunWarning, runsOnPr, type MergeRunWarning } from './merge-run-warning';
import { isOpenPr, planPrActions, type TaskActionPlan } from './task-pr-actions';
import { prLabel } from './task-types';

/** A task's pull requests live, merging them, and the task panel's PR actions (TaskCardPrs, TaskPrActions). */

/** One of the task panel's actions, ready to show: a plan with its press. */
export interface TaskAction extends TaskActionPlan {
  loading?: boolean;
  /** SF-T-237: on Merge, a run still going on the task (mergeRunNote): a warning icon and this hint. */
  runNote?: { tone: 'warning' | 'info'; text: string } | null;
  onSelect: () => void;
}

/**
 * A task's pull requests as GitHub sees them now (T-3), for its card and its panel (SF-T-79): read
 * again when GitHub's webhook says they changed (T-13), and shortly after while GitHub is still working
 * out whether they merge. SF-T-154: `followTask` (a task with sub-tasks) reads them again when the task
 * changes too, which a change to one of its sub-tasks is sent as, so Merge on a PR they share comes on
 * once the last of them is ready.
 */
export function useTaskPrsLive(taskId: string, prs: TaskPr[], options: { followTask?: boolean } = {}): { live: TaskPrsLiveResponse | null; reload: () => void } {
  const projectId = useCurrentProject()?.id;
  const anyOpen = prs.some(isOpenPr);
  const signature = prs.map((p) => `${p.url}=${p.status}`).join(',');
  // GitHub's webhook says when they change (T-13): a new tick reads them again, whatever staleMs says.
  const tick = useTaskStore((s) => `${s.prsTick}.${s.prTicks[taskId] ?? 0}.${options.followTask ? (s.taskTicks[taskId] ?? 0) : 0}`);
  const loader = useLoader((signal) => getTaskPrsLive(taskId, { signal }), [taskId, signature, tick], {
    key: `${projectId}:task-prs:${taskId}:${signature}:${tick}`,
    enabled: anyOpen && !!projectId,
    staleMs: 60_000,
    // GitHub works mergeability out after a push; look again shortly while it is.
    pollMs: 5_000,
    pollWhile: (res) => !!res?.data?.prs.some((p) => p.live && prMergeView(p.live) === 'checking'),
    pollLimit: 6,
  });
  return { live: loader.data?.data ?? null, reload: loader.reload };
}

/**
 * Merging one of the task's pull requests, after asking (T-3): `ask` opens the confirmation, `dialog`
 * goes in the page. `reload` reads the PRs again after a refusal (GitHub's state moved on). SF-T-237: as
 * it opens, it looks for a run still going that may push more to the PR (on the task, or on the PR's branch,
 * such as a sub-task's run on the PR it shares) and warns, with Merge anyway; Merge waits for that look. If
 * it fails, it says so with Retry, and Merge still works.
 */
export function usePrMerge(taskId: string, reload: () => void): { ask: (pr: TaskPr, live: GithubPrLive) => void; dialog: ReactNode } {
  const projectSlug = useCurrentProject()?.slug;
  const [confirming, setConfirming] = useState<{ pr: TaskPr; live: GithubPrLive } | null>(null);
  const [merging, setMerging] = useState(false);
  // The run check for the confirmation that's open: only the newest one's answer is kept.
  const [runCheck, setRunCheck] = useState<RunCheck>(NO_RUN_CHECK);
  const checkSeq = useRef(0);

  const checkRuns = (on: { pr: TaskPr; live: GithubPrLive }, retrying: boolean) => {
    const seq = ++checkSeq.current;
    setRunCheck((last) => ({ checking: true, warning: null, error: retrying ? last.error : null }));
    // The project's unfinished runs (few), so a run on another task pushing to this PR's branch counts too.
    void listRuns({ status: ACTIVE_RUN_STATUSES, limit: 100 }).then((res) => {
      if (seq !== checkSeq.current) return;
      const onPr = res.data ? runsOnPr(res.data.runs, { taskId, prUrl: on.pr.url, headRef: on.live.head_ref }) : [];
      setRunCheck(
        res.data
          ? { checking: false, warning: mergeRunWarning(onPr, taskId), error: null }
          : { checking: false, warning: null, error: describeApiError(res, "The runs didn't load") }
      );
    });
  };
  const ask = (pr: TaskPr, live: GithubPrLive) => {
    setConfirming({ pr, live });
    checkRuns({ pr, live }, false);
  };
  const close = () => {
    checkSeq.current++;
    setConfirming(null);
    setRunCheck(NO_RUN_CHECK);
  };

  const merge = async () => {
    if (!confirming) return;
    setMerging(true);
    const res = await mergeTaskPr(taskId, confirming.pr.url);
    setMerging(false);
    if (res.data) {
      toast.success(`Merged ${prLabel(confirming.pr.url)}`);
      close();
      // The card itself updates through the live Board (the task's PR is now marked merged).
      return;
    }
    toast.error(describeApiError(res, "Couldn't merge it"));
    close();
    reload();
  };
  const { warning } = runCheck;
  const notice = warning ? (
    <AppAlert compact tone={warning.tone} title={warning.title} data-testid="merge-run-warning">
      {warning.text}{' '}
      {projectSlug ? (
        <Link to={runPath(projectSlug, warning.run.number)} onClick={close} className="font-medium underline underline-offset-2">
          Open the run
        </Link>
      ) : null}
    </AppAlert>
  ) : runCheck.error ? (
    <LoadError
      compact
      title="Couldn't check whether a run is still working on this task"
      error={runCheck.error}
      onRetry={() => confirming && checkRuns(confirming, true)}
      retrying={runCheck.checking}
    />
  ) : null;
  const dialog = (
    <ConfirmDialog
      open={!!confirming}
      title={confirming ? `Merge ${prLabel(confirming.pr.url)}?` : 'Merge'}
      description={
        confirming
          ? `“${confirming.live.title}” will be merged into its base branch on GitHub, as the repo's merge setting says. Merging can't be undone from here.`
          : ''
      }
      notice={notice}
      confirmLabel={warning ? 'Merge anyway' : 'Merge'}
      confirmDisabled={runCheck.checking}
      submitting={merging}
      onOpenChange={(open) => !open && close()}
      onConfirm={() => void merge()}
    />
  );
  return { ask, dialog };
}

const ACTIVE_RUN_STATUSES = ['queued', 'running', 'waiting'] as const;
/** The look for a run still going, for the open confirmation: `error` when it failed (Retry looks again). */
type RunCheck = { checking: boolean; warning: MergeRunWarning | null; error: string | null };
const NO_RUN_CHECK: RunCheck = { checking: false, warning: null, error: null };

/**
 * SF-T-183: Update: hands a PR adding migrations, while main has newer ones, to the AI builder (merge main
 * in, `pnpm db:rebase`). `updating` is the PR being handed over; `reload` reads the PRs again after a refusal.
 */
export function usePrUpdate(taskId: string, reload: () => void): { update: (url: string) => Promise<void>; updating: string | null } {
  const [updating, setUpdating] = useState<string | null>(null);
  const update = async (url: string) => {
    setUpdating(url);
    const res = await updateTaskPr(taskId, url);
    setUpdating(null);
    if (res.data) {
      toast.success(
        res.data.kind === 'conflicts' ? `The AI builder is fixing ${prLabel(url)}'s conflicts` : `The AI builder is bringing ${prLabel(url)} up to date`
      );
      return;
    }
    toast.error(describeApiError(res, "Couldn't hand it to the AI builder"));
    reload();
  };
  return { update, updating };
}

const NO_PRS: TaskPr[] = [];

/**
 * SF-T-79: the task panel's pull request actions: Merge each open PR (on when GitHub says it's ready and
 * its checks passed) and Fix conflicts (hands a conflicting PR to the AI builder). SF-T-183: Update hands a
 * PR adding migrations, while main has newer ones, to the AI builder. `dialog` (the merge
 * confirmation) goes in the page. SF-T-154: a sub-task's PR its parent shares gets neither: it's the
 * parent's to merge and fix. With the column moves (planMoveActions), the toolbar shows them as
 * buttons (TaskPrActionButtons); phones list them in the More menu (TaskPrActionMenuItems). `task` may be
 * null while it loads.
 */
export function useTaskPrActions(
  task: { id: string; prs: TaskPr[]; waiting_on: TaskWaiting[]; parent?: TaskParent | null; subtasks?: TaskSubtaskCount | null } | null
) {
  const may = { merge: usePermission('PRS_MERGE'), run: usePermission('RUNS_START') };
  const taskId = task?.id ?? '';
  const prs = task?.prs ?? NO_PRS;
  const { live, reload } = useTaskPrsLive(taskId, prs, { followTask: !!task?.subtasks });
  const merging = usePrMerge(taskId, reload);
  const updating = usePrUpdate(taskId, reload);
  const [fixing, setFixing] = useState<string | null>(null);

  const fix = async (url: string) => {
    setFixing(url);
    const res = await fixTaskPrConflicts(taskId, url);
    setFixing(null);
    if (res.data) toast.success(`The AI builder is fixing ${prLabel(url)}'s conflicts`);
    else toast.error(describeApiError(res, "Couldn't hand it to the AI builder"));
  };

  const plans = task ? planPrActions({ prs, live, waiting: task.waiting_on, may, parent: task.parent }) : [];
  const actions: TaskAction[] = plans.map((plan) => ({
    ...plan,
    loading: (plan.kind === 'fix_conflicts' && fixing === plan.prUrl) || (plan.kind === 'update' && updating.updating === plan.prUrl),
    onSelect: () => {
      if (plan.kind === 'fix_conflicts') return void fix(plan.prUrl!);
      if (plan.kind === 'update') return void updating.update(plan.prUrl!);
      const rowLive = live?.prs.find((p) => p.url === plan.prUrl)?.live;
      const pr = prs.find((p) => p.url === plan.prUrl);
      if (pr && rowLive) merging.ask(pr, rowLive);
    },
  }));
  return { actions, dialog: merging.dialog };
}

